import { useState } from 'react';
import { QuyDoiDonViTinhChung, QuyDoiDonViTinhChungInput } from '@/types/schemas/quy-doi-don-vi-tinh-chung.type';
import { ActionBar, DeleteDialog, UnitDialog } from './components';
import { ErpDataTableV2 } from '@/components/custom/erp';
import { getDataTableColumns } from './cols-definition';
import { useCRUD, useForms } from '@/hooks';
import { QUERY_KEYS } from '@/constants';
import { useSearchTable } from './hooks';

const PAGE_SIZE = 20;

export default function QuyDoiDonViTinhChungPage() {
  const [formErrors, setFormErrors] = useState<{
    dvt?: string;
    dvt0?: string;
    he_so?: string;
  }>({});

  const {
    data: quyDoiDonViTinhChungs,
    totalItems,
    currentPage,
    detail,
    fetchDetail,
    handlePageChange,
    addItem: addQuyDoiDonViTinhChung,
    updateItem: updateQuyDoiDonViTinhChung,
    deleteItem: deleteQuyDoiDonViTinhChung,
    refreshData: refreshQuyDoiDonViTinhChungs,
    searchData
  } = useCRUD<QuyDoiDonViTinhChung, QuyDoiDonViTinhChungInput>({
    endpoint: QUERY_KEYS.QUY_DOI_DON_VI_CHUNG,
    pageSize: PAGE_SIZE
  });

  const {
    showForm,
    showDelete,
    formMode,
    selectedObj,
    selectedRowIndex,
    isCopyMode,

    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    handleRowClick,
    clearSelection
  } = useForms<QuyDoiDonViTinhChung>(fetchDetail);

  const { searchState, handleSearchChange, onPageChange, hasFilterParams } = useSearchTable({
    searchData,
    isFiltered: false,
    handlePageChange: handlePageChange
  });

  const displayData = hasFilterParams ? searchState.searchResults : quyDoiDonViTinhChungs || [];
  const resolvedCurrentPage = hasFilterParams ? searchState.currentPage : currentPage;
  const resolvedTotalItems = hasFilterParams ? searchState.totalCount : totalItems;

  const tables = [
    {
      name: '',
      rows: displayData,
      columns: getDataTableColumns()
    }
  ];

  const handleFormSubmit = async (data: QuyDoiDonViTinhChungInput) => {
    try {
      // Kiểm tra nếu có lỗi từ component con
      if (data._errors) {
        setFormErrors(data._errors);
        // Mở lại form để hiển thị lỗi
        handleAddClick();
        return;
      }

      // Reset errors
      setFormErrors({});

      // Tiếp tục xử lý như bình thường
      if (formMode === 'add') {
        await addQuyDoiDonViTinhChung(data);
      } else if (formMode === 'edit' && selectedObj) {
        await updateQuyDoiDonViTinhChung(selectedObj.uuid, data);
      }

      refreshQuyDoiDonViTinhChungs();
      handleCloseForm();
      clearSelection();
    } catch (error: unknown) {
      // Xử lý lỗi từ API
      console.error('Error submitting form:', error);

      // Hiển thị lỗi cụ thể
      const newErrors: {
        dvt?: string;
        dvt0?: string;
        he_so?: string;
      } = {};

      const apiError = error as { response?: { data?: { detail?: string; error?: string } } };
      if (apiError.response) {
        if (apiError.response.data?.detail) {
          newErrors.dvt = `Lỗi: ${apiError.response.data.detail}`;
        } else if (apiError.response.data?.error) {
          newErrors.dvt = `Lỗi: ${apiError.response.data.error}`;
        } else {
          newErrors.dvt = 'Có lỗi xảy ra khi lưu dữ liệu. Vui lòng thử lại sau.';
        }
      } else {
        newErrors.dvt = 'Có lỗi xảy ra khi lưu dữ liệu. Vui lòng thử lại sau.';
      }

      setFormErrors(newErrors);
      // Mở lại form để hiển thị lỗi
      handleAddClick();
    }
  };

  return (
    <div className='flex h-full min-h-[calc(100vh-120px)] w-full flex-col'>
      <ActionBar
        onAddClick={handleAddClick}
        onEditClick={() => selectedObj && handleEditClick()}
        onDeleteClick={() => selectedObj && handleDeleteClick()}
        onCopyClick={() => selectedObj && handleCopyClick()}
        onViewClick={() => selectedObj && handleViewClick()}
        onRefreshClick={refreshQuyDoiDonViTinhChungs}
      />

      <ErpDataTableV2
        tables={tables}
        onRowClick={handleRowClick}
        selectedRowId={selectedRowIndex || undefined}
        totalItems={resolvedTotalItems}
        currentPage={resolvedCurrentPage}
        onPageChange={onPageChange}
        pageSize={PAGE_SIZE}
        onSearchChange={handleSearchChange}
        getRowId={row => row.uuid}
      />

      {showForm && (
        <UnitDialog
          open={showForm}
          mode={formMode}
          initialData={formMode === 'add' && !isCopyMode ? undefined : detail}
          onClose={handleCloseForm}
          selectedObj={formMode === 'edit' || formMode === 'view' ? selectedObj : null}
          onSubmit={handleFormSubmit}
          onAddButtonClick={handleAddClick}
          onEditButtonClick={handleEditClick}
          onDeleteButtonClick={handleDeleteClick}
          onCopyButtonClick={handleCopyClick}
          errors={formErrors}
        />
      )}

      {showDelete && (
        <DeleteDialog
          open={showDelete}
          onClose={handleCloseDelete}
          selectedObj={selectedObj}
          deleteUnit={deleteQuyDoiDonViTinhChung}
          clearSelection={clearSelection}
        />
      )}
    </div>
  );
}
